import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  CssBaseline,
  Link,
  FormGroup,
  Breadcrumbs,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Card,
  Stack,
  IconButton,
} from '@mui/material';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { ArrowBack } from '@mui/icons-material';
import AppToast from '../../components/Toast/AppToast';

const daysOfWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

// Utility to get current IST time in HH:mm format
const getCurrentISTTime = () => {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const istTime = new Date(utc + 5.5 * 60 * 60 * 1000);
  return istTime.toTimeString().substring(0, 5);
};

// Initialize form values with default IST times
const initFormWithTimes = () => {
  const defaultTime = getCurrentISTTime();
  return {
    diet_name: '',
    preworkout: '',
    preworkout_time: defaultTime,
    post_workout: '',
    post_workout_time: defaultTime,
    breakfast: '',
    breakfast_time: defaultTime,
    morning_snaks: '',
    morning_snaks_time: defaultTime,
    evening_snaks1: '',
    evening_snaks1_time: defaultTime,
    evening_snaks2: '',
    evening_snaks2_time: defaultTime,
    dinner: '',
    dinner_time: defaultTime,
    meal1: '',
    meal1_time: defaultTime,
    meal2: '',
    meal2_time: defaultTime,
  };
};

const DietPlanForm = () => {
  const { colors: appColors } = useAppTheme();
  const navigate = useNavigate();
  const [days, setDays] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [formValues, setFormValues] = React.useState(initFormWithTimes);

  const handleCheckboxChange = (event) => {
    const { value, checked } = event.target;
    setDays((prev) =>
      checked ? [...prev, value] : prev.filter((day) => day !== value)
    );
  };
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success', // 'success' | 'error'
  });
  
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  
  const handleSelectAll = () => {
    if (selectAll) {
      setDays([]);
    } else {
      setDays(daysOfWeek);
    }
    setSelectAll(!selectAll);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const data = {
      ...formValues,
      days,
    };
    const userData = JSON.parse(localStorage.getItem('userdata'));
    const userToken = userData?.token;
  
    try {
      const response = await fetch(`${window.constants.api_url}/create/diet-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(data),
      });
  
      const result = await response.json();
  
      if (response.ok) {
        setSnackbar({ open: true, message: "Diet plan created successfully!", severity: 'success' });
        navigate('/diet-chart');
      } else {
        setSnackbar({ open: true, message: result.message || "Failed to create diet plan.", severity: 'error' });
      }
    } catch (error) {
      console.error("Error:", error);
      setSnackbar({ open: true, message: "Something went wrong. Please try again later.", severity: 'error' });
    }
  };
  
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <CssBaseline />
      <Header />

      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
        <Container maxWidth="xl">
          {/* Header Section */}
          <Card sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    bgcolor: appColors.evergreen,
                    color: 'white',
                    '&:hover': { bgcolor: appColors.pine },
                  }}
                >
                  <ArrowBack />
                </IconButton>
                <Box>
                  <Typography variant="h5" sx={{ color: appColors.evergreen, fontWeight: 600 }}>
                    Create Diet Plan
                  </Typography>
                  <Breadcrumbs sx={{ mt: 0.5 }}>
                    <Link underline="hover" color="inherit" href="#">
                      Home
                    </Link>
                    <Typography color="text.primary">Create Diet Plan</Typography>
                  </Breadcrumbs>
                </Box>
              </Stack>
              <Button
                variant="contained"
                onClick={() => navigate('/diet-chart')}
                sx={{
                  bgcolor: appColors.evergreen,
                  '&:hover': { bgcolor: appColors.pine },
                  textTransform: 'none',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                View Diet Plans
              </Button>
            </Stack>
          </Card>

          {/* Form Section */}
          <Card sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
            {/* Days Selection */}
            <Typography variant="subtitle1" sx={{ color: appColors.evergreen, fontWeight: 600, mb: 2 }}>
              Select Days
            </Typography>
            <FormGroup 
              row 
              sx={{ 
                mb: 4,
                gap: 2,
                '.MuiFormControlLabel-root': {
                  mr: 0,
                  minWidth: 100,
                }
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={selectAll} 
                    onChange={handleSelectAll}
                    sx={{
                      color: appColors.evergreen,
                      '&.Mui-checked': {
                        color: appColors.evergreen,
                      },
                    }}
                  />
                }
                label="Select All"
              />
              {daysOfWeek.map((day, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={days.includes(day)}
                      onChange={handleCheckboxChange}
                      value={day}
                      sx={{
                        color: appColors.evergreen,
                        '&.Mui-checked': {
                          color: appColors.evergreen,
                        },
                      }}
                    />
                  }
                  label={day}
                />
              ))}
            </FormGroup>

            {/* Diet Plan Form */}
            <Grid container spacing={3}>
              {[
                ['Diet Name', 'diet_name'],
                ['Pre-Workout', 'preworkout', 'preworkout_time'],
                ['Post-Workout', 'post_workout', 'post_workout_time'],
                ['Breakfast', 'breakfast', 'breakfast_time'],
                ['Morning Snacks', 'morning_snaks', 'morning_snaks_time'],
                ['Evening Snacks 1', 'evening_snaks1', 'evening_snaks1_time'],
                ['Evening Snacks 2', 'evening_snaks2', 'evening_snaks2_time'],
                ['Dinner', 'dinner', 'dinner_time'],
                ['Meal 1', 'meal1', 'meal1_time'],
                ['Meal 2', 'meal2', 'meal2_time'],
              ].map(([label, name, timeName], index) => (
                <React.Fragment key={index}>
                  <Grid size={{ xs: 12, md:timeName ? 8 : 12}}>
                    <TextField
                      fullWidth
                      label={label}
                      name={name}
                      value={formValues[name]}
                      onChange={handleInputChange}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: appColors.evergreen,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: appColors.evergreen,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: appColors.evergreen,
                        },
                      }}
                    />
                  </Grid>
                  {timeName && (
                    <Grid size={{ xs: 12,md:4}}>
                      <TextField
                        fullWidth
                        label="Time"
                        name={timeName}
                        type="time"
                        value={formValues[timeName]}
                        onChange={handleInputChange}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: appColors.evergreen,
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: appColors.evergreen,
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: appColors.evergreen,
                          },
                        }}
                      />
                    </Grid>
                  )}
                </React.Fragment>
              ))}

              <Grid size={{ xs: 12}}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  onClick={handleSubmit}
                  sx={{
                    bgcolor: appColors.evergreen,
                    '&:hover': { bgcolor: appColors.pine },
                    textTransform: 'none',
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 6px rgba(31, 122, 84, 0.12)',
                    '&:hover': {
                      bgcolor: appColors.pine,
                      boxShadow: '0 6px 8px rgba(31, 122, 84, 0.2)',
                    },
                  }}
                >
                  Create Diet Plan
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      <AppToast open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={handleCloseSnackbar} />
    </Box>
  );
};

export default DietPlanForm;
